import { useCallback, useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/widgets";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import dayjs from "dayjs";
import { CalendarRange, Factory, Lightbulb, Loader2, Medal, Printer, RefreshCw, Star } from "lucide-react";
import { cn } from "@/lib/utils";
import { StarRating, RatingDelta } from "@/components/ratings/StarRating";
import { TierBadge, MomDelta } from "@/components/oem/TierBadge";

/* Google Ratings month-end table per function + 3-month weighted trend */
const MonthlyOemSection = ({ section }) => {
  if (!section || !(section.rows || []).length) return null;
  return (
    <Card data-testid="monthly-oem-section">
      <CardHeader className="pb-2">
        <CardTitle className="font-serif text-base font-normal flex items-center gap-1.5">
          <Medal size={15} className="text-[hsl(var(--cx-gold))]" />
          {section.label}
          <span className="text-[0.65rem] text-muted-foreground font-sans font-normal ml-1">
            group avg {section.group_avg ?? "—"} · OEM lens, never affects complaint accountability
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 px-0">
        <div className="overflow-x-auto">
          <Table className="cx-table min-w-[680px]" data-testid="monthly-oem-table">
            <TableHeader>
              <TableRow>
                <TableHead className="pl-4">#</TableHead>
                <TableHead>Location</TableHead>
                <TableHead className="text-right">Score</TableHead>
                <TableHead>Tier</TableHead>
                <TableHead className="text-right">Sales</TableHead>
                <TableHead className="text-right">Service</TableHead>
                <TableHead className="text-right">National</TableHead>
                <TableHead className="text-right">Regional</TableHead>
                <TableHead className="text-right pr-4">MoM</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {section.rows.map((r) => (
                <TableRow key={r.code} data-testid={`monthly-oem-row-${r.code}`}>
                  <TableCell className="pl-4 tabular">{r.rank}</TableCell>
                  <TableCell className="font-medium">{r.name} <span className="text-xs text-muted-foreground">{r.code}</span></TableCell>
                  <TableCell className="text-right tabular font-semibold">{r.score !== null ? Math.round(r.score) : "—"}</TableCell>
                  <TableCell><TierBadge tier={r.tier} /></TableCell>
                  <TableCell className="text-right tabular text-xs">{r.sales_score ?? "—"}</TableCell>
                  <TableCell className="text-right tabular text-xs">{r.service_score ?? "—"}</TableCell>
                  <TableCell className="text-right tabular text-xs">{r.national_rank ?? "—"}</TableCell>
                  <TableCell className="text-right tabular text-xs">{r.regional_rank ?? "—"}</TableCell>
                  <TableCell className="text-right pr-4"><MomDelta value={r.mom_delta} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {(section.group_focus || []).length > 0 && (
          <div className="px-5 pt-3" data-testid="monthly-oem-focus">
            <p className="text-[0.65rem] uppercase tracking-[0.1em] font-medium text-[hsl(var(--cx-crimson))]">Group focus areas</p>
            {section.group_focus.map((line, i) => (
              <p key={i} className="text-[0.68rem] text-muted-foreground mt-1 border-l-2 border-l-[hsl(var(--cx-crimson)/0.5)] pl-2">{line}</p>
            ))}
          </div>
        )}
        {(section.trend || []).length > 1 && (
          <p className="px-5 pt-2 pb-1 text-[0.68rem] text-muted-foreground tabular">
            Group average trend: {section.trend.map((t) => `${t.label} ${t.avg}`).join(" → ")}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

const MonthlyGoogleRatings = ({ section }) => {
  const fns = Object.keys(section?.functions || {}).filter((fn) => (section.functions[fn] || []).length > 0);
  if (fns.length === 0) return null;
  return (
    <Card data-testid="monthly-google-ratings-section">
      <CardHeader className="pb-2">
        <CardTitle className="font-serif text-base font-normal flex items-center gap-1.5">
          <Star size={15} className="text-[hsl(var(--cx-gold))]" fill="currentColor" strokeWidth={0} />
          Google Ratings — month end
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 px-0">
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 px-0">
          {fns.map((fn) => (
            <div key={fn}>
              <p className="px-5 pb-1 text-[0.65rem] uppercase tracking-[0.12em] text-muted-foreground font-medium">{fn}</p>
              <Table className="cx-table" data-testid={`monthly-google-table-${fn.toLowerCase()}`}>
                <TableHeader>
                  <TableRow>
                    <TableHead className="pl-4">#</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Rating</TableHead>
                    <TableHead className="text-right">vs last mo.</TableHead>
                    <TableHead className="text-right">Reviews</TableHead>
                    <TableHead className="text-right">New</TableHead>
                    <TableHead className="text-right pr-4">Neg</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {section.functions[fn].slice(0, 10).map((r) => (
                    <TableRow key={r.outlet_id}>
                      <TableCell className="pl-4 tabular">{r.rank}</TableCell>
                      <TableCell><span className="font-medium">{r.name}</span> <span className="text-xs text-muted-foreground">{r.code}</span></TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-1.5 tabular font-semibold">
                          {r.rating?.toFixed(1) ?? "—"} <StarRating value={r.rating} size={11} />
                        </span>
                      </TableCell>
                      <TableCell className="text-right"><RatingDelta delta={r.delta_vs_last_month} /></TableCell>
                      <TableCell className="text-right tabular">{r.total_reviews?.toLocaleString() ?? "—"}</TableCell>
                      <TableCell className="text-right tabular">+{r.new_reviews}</TableCell>
                      <TableCell className="text-right tabular pr-4" data-testid={`monthly-google-neg-${r.code}`}>
                        {r.negative_reviews > 0
                          ? <span className="text-[hsl(var(--cx-crimson))] font-semibold">{r.negative_reviews}</span>
                          : <span className="text-muted-foreground">0</span>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {(section.trend?.[fn] || []).length > 0 && (
                <p className="px-5 pt-2 text-[0.68rem] text-muted-foreground tabular">
                  Group weighted average:{" "}
                  {section.trend[fn].map((t) => `${dayjs(t.month + "-01").format("MMM")} ${t.weighted_avg ?? "—"}`).join(" → ")}
                </p>
              )}
              {(section.negative_excerpts?.[fn] || []).length > 0 && (
                <div className="px-5 pt-2" data-testid={`monthly-google-negatives-${fn.toLowerCase()}`}>
                  <p className="text-[0.65rem] uppercase tracking-[0.1em] font-medium text-[hsl(var(--cx-crimson))]">Recent negative reviews</p>
                  {section.negative_excerpts[fn].map((n, i) => (
                    <p key={`${fn}-neg-${i}`} className="text-[0.68rem] text-muted-foreground mt-1 border-l-2 border-l-[hsl(var(--cx-crimson)/0.5)] pl-2">
                      <span className="font-semibold text-[hsl(var(--cx-ink))]">{n.rating}★ · {n.outlet_name}</span>{" "}
                      · {n.author_display} · {n.date_fmt} — {n.excerpt}
                    </p>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        <p className="px-5 pt-2 pb-1 text-[0.65rem] text-muted-foreground">
          Recognition only — Google ratings never affect complaint standings, escalation or tone tiers.
        </p>
      </CardContent>
    </Card>
  );
};

export default function MonthlyReportsPage() {
  const { user } = useAuth();
  const [archive, setArchive] = useState(null);
  const [selected, setSelected] = useState(null);
  const [report, setReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [generating, setGenerating] = useState(false);
  const isAdmin = user?.permissions?.can_manage_settings;

  const loadArchive = useCallback(() => {
    api.get("/reports/monthly").then((r) => {
      setArchive(r.data);
      setSelected((prev) => {
        if (prev) {
          const still = r.data.find((a) => a.id === prev.id);
          if (still) return still;
        }
        return r.data.find((a) => a.status !== "superseded") || r.data[0] || null;
      });
    }).catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(() => { loadArchive(); }, [loadArchive]);

  useEffect(() => {
    if (!selected) return;
    setLoadingReport(true);
    const params = selected.status === "superseded" ? { report_id: selected.id } : {};
    api.get(`/reports/monthly/${selected.month}`, { params })
      .then((r) => setReport(r.data))
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoadingReport(false));
  }, [selected]);

  const generate = async (month) => {
    setGenerating(true);
    try {
      const r = await api.post("/reports/monthly/generate", { month: month || null });
      toast.success(`Monthly report generated for ${r.data.label}`);
      setSelected({ month: r.data.month, id: r.data.id, status: r.data.status || "current" });
      loadArchive();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setGenerating(false);
    }
  };

  if (archive === null)
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-72" />
      </div>
    );

  const k = report?.kpis;

  return (
    <div data-testid="monthly-reports-page">
      <PageHeader
        title="Monthly Reports"
        subtitle="Month-end archive · auto-generated on the 1st at 09:00 IST · final standings are frozen per month"
      >
        {isAdmin && (
          <Button variant="outline" onClick={() => generate(null)} disabled={generating} data-testid="monthly-generate-button">
            {generating ? <Loader2 size={15} className="animate-spin mr-1.5" /> : <RefreshCw size={15} className="mr-1.5" />}
            Generate Last Month
          </Button>
        )}
        {report && (
          <Button variant="outline" onClick={() => window.print()} data-testid="monthly-print-button">
            <Printer size={15} className="mr-1.5" /> Print / PDF
          </Button>
        )}
      </PageHeader>

      {archive.length === 0 ? (
        <Card>
          <CardContent className="py-14">
            <EmptyState
              title="No monthly reports yet"
              subtitle={isAdmin ? "Generate last month's report with the button above, or wait for the automatic month-end run." : "Reports appear automatically after each month closes."}
            />
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Archive list */}
          <Card className="lg:col-span-3 h-fit print:hidden" data-testid="monthly-archive-panel">
            <CardHeader className="pb-2">
              <CardTitle className="font-serif text-base font-normal flex items-center gap-1.5">
                <CalendarRange size={15} /> Archive
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 px-2 pb-3 space-y-1">
              {archive.map((a) => (
                <button key={a.id}
                  className={cn(
                    "w-full text-left px-3 py-2 rounded-md transition-colors hover:bg-[hsl(var(--cx-surface))]",
                    selected?.id === a.id && "bg-[hsl(var(--accent))] border border-[hsl(var(--cx-gold)/0.4)]"
                  )}
                  data-testid={`monthly-archive-item-${a.month}-v${a.version || 1}`}
                  onClick={() => setSelected(a)}>
                  <p className="text-sm font-medium flex items-center gap-1.5">
                    {a.label}
                    {a.status === "superseded" ? (
                      <Badge variant="outline" className="text-[0.6rem] text-muted-foreground">Superseded</Badge>
                    ) : (
                      <Badge className="text-[0.6rem] bg-[hsl(var(--status-green))] text-white border-transparent">Current</Badge>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground tabular">
                    {a.kpis?.opened ?? 0} opened · {a.kpis?.closed ?? 0} closed · {a.trigger === "scheduled" ? "auto" : "manual"}
                  </p>
                  <p className="text-[0.62rem] text-muted-foreground tabular">Generated: {a.generated_on_fmt || dayjs(a.generated_at).format("DD MMM YYYY, h:mm A")}</p>
                  <p className="text-[0.62rem] text-muted-foreground tabular">Data: {a.based_on_fmt || "—"}</p>
                </button>
              ))}
            </CardContent>
          </Card>

          {/* Report body */}
          <div className="lg:col-span-9 space-y-4" data-testid="monthly-report-body">
            {loadingReport || !report ? (
              <Skeleton className="h-96" />
            ) : (
              <>
                <div className="flex items-end justify-between flex-wrap gap-2">
                  <div>
                    <h2 className="font-serif text-2xl text-[hsl(var(--cx-ink))] flex items-center gap-2" data-testid="monthly-report-title">
                      {report.label} — Month-End Review
                      {report.status === "superseded" && (
                        <Badge variant="outline" className="text-muted-foreground">Superseded</Badge>
                      )}
                    </h2>
                    <p className="text-xs text-muted-foreground tabular" data-testid="monthly-report-stamps">
                      Generated on: {report.generated_on_fmt || dayjs(report.generated_at).format("DD MMM YYYY, h:mm A")}
                      {" · "}Based on data uploaded: {report.based_on_fmt || "—"}
                      {" · "}{report.trigger === "scheduled" ? "automatic month-end run" : `manual by ${report.generated_by}`}
                    </p>
                  </div>
                  {isAdmin && (
                    <Button variant="ghost" size="sm" className="text-xs print:hidden" onClick={() => generate(report.month)} disabled={generating}
                      data-testid="monthly-regenerate-button">
                      <RefreshCw size={13} className="mr-1" /> Regenerate
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    ["Opened", k.opened, null],
                    ["Closed", k.closed, `${k.closure_rate}% closure rate`],
                    ["Avg Resolution", k.avg_resolution_days ?? "—", "days"],
                    ["Reopens", k.reopens, `${k.reopen_pct}% of closures`],
                  ].map(([label, val, sub]) => (
                    <Card key={label} className="p-4" data-testid={`monthly-kpi-${String(label).toLowerCase().replace(/ /g, "-")}`}>
                      <p className="text-[0.62rem] uppercase tracking-[0.12em] text-muted-foreground font-medium">{label}</p>
                      <p className="kpi-value text-2xl sm:text-3xl mt-1">{val}</p>
                      {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
                    </Card>
                  ))}
                </div>

                {/* This Month in Brief — rules-based insights (informational) */}
                {report.insights_brief?.length > 0 && (
                  <Card data-testid="monthly-insights-brief">
                    <CardContent className="py-3.5">
                      <p className="flex items-center gap-1.5 text-xs uppercase tracking-[0.12em] font-medium text-[hsl(var(--cx-navy))] mb-2">
                        <Lightbulb size={14} className="text-[hsl(var(--cx-gold))]" /> This Month in Brief
                      </p>
                      <ul className="space-y-1.5">
                        {report.insights_brief.map((line, i) => (
                          <li key={`brief-${i}`} className="text-sm flex gap-2" data-testid={`monthly-brief-line-${i}`}>
                            <span className="text-[hsl(var(--cx-gold))] shrink-0">·</span>{line}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                <Card data-testid="monthly-confirmation-panel">
                  <CardContent className="py-3 flex flex-wrap items-center gap-x-6 gap-y-2">
                    <span className="text-xs uppercase tracking-[0.12em] text-muted-foreground font-medium">Function split</span>
                    <span className="text-sm tabular">
                      Sales {report.function_split?.Sales?.opened ?? 0} opened / {report.function_split?.Sales?.closed ?? 0} closed
                    </span>
                    <span className="text-sm tabular">
                      Service {report.function_split?.Service?.opened ?? 0} opened / {report.function_split?.Service?.closed ?? 0} closed
                    </span>
                  </CardContent>
                </Card>

                <div className="rounded-[var(--radius)] bg-[hsl(var(--cx-navy))] text-white px-4 py-3 flex flex-wrap items-center gap-x-6 gap-y-1" data-testid="monthly-oem-summary">
                  <span className="flex items-center gap-2 text-xs uppercase tracking-[0.12em] font-medium text-white/80">
                    <Factory size={14} className="text-[hsl(var(--cx-gold))]" /> OEM delays (separate lane)
                  </span>
                  <span className="text-sm tabular">{report.oem_summary.new} raised</span>
                  <span className="text-sm tabular">{report.oem_summary.closed} resolved</span>
                  <span className="text-sm tabular">{report.oem_summary.open_at_month_end} open at month end</span>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                  <Card data-testid="monthly-closure-standings">
                    <CardHeader className="pb-2"><CardTitle className="font-serif text-base font-normal">Final Closure Standings</CardTitle></CardHeader>
                    <CardContent className="pt-0 px-0">
                      <Table className="cx-table">
                        <TableHeader>
                          <TableRow>
                            <TableHead className="pl-4">#</TableHead>
                            <TableHead>Outlet</TableHead>
                            <TableHead className="text-right">Genuine</TableHead>
                            <TableHead className="text-right">Rate</TableHead>
                            <TableHead className="text-right pr-4">Reopens</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {report.closure_leaderboard.slice(0, 10).map((o) => (
                            <TableRow key={o.outlet_id}>
                              <TableCell className="pl-4 tabular">{o.rank}</TableCell>
                              <TableCell><span className="font-medium">{o.outlet_name}</span> <span className="text-xs text-muted-foreground">{o.outlet_code}</span></TableCell>
                              <TableCell className="text-right tabular font-semibold">{o.genuine_closures}</TableCell>
                              <TableCell className="text-right tabular">{o.genuine_rate}%</TableCell>
                              <TableCell className="text-right tabular pr-4">{o.reopens}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>

                  <Card data-testid="monthly-prevention-standings">
                    <CardHeader className="pb-2"><CardTitle className="font-serif text-base font-normal">Final Prevention Standings — fewest opened</CardTitle></CardHeader>
                    <CardContent className="pt-0 px-0">
                      <Table className="cx-table">
                        <TableHeader>
                          <TableRow>
                            <TableHead className="pl-4">#</TableHead>
                            <TableHead>Outlet</TableHead>
                            <TableHead className="text-right">Opened</TableHead>
                            <TableHead className="text-right pr-4">Prev Month</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {report.prevention_leaderboard.slice(0, 10).map((o) => (
                            <TableRow key={o.outlet_id}>
                              <TableCell className="pl-4 tabular">{o.rank}</TableCell>
                              <TableCell><span className="font-medium">{o.outlet_name}</span> <span className="text-xs text-muted-foreground">{o.outlet_code}</span></TableCell>
                              <TableCell className="text-right tabular font-semibold">{o.opened}</TableCell>
                              <TableCell className="text-right tabular pr-4 text-muted-foreground">{o.opened_prev}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                  <Card data-testid="monthly-handler-standings">
                    <CardHeader className="pb-2"><CardTitle className="font-serif text-base font-normal">Best Performers — Genuine Closures</CardTitle></CardHeader>
                    <CardContent className="pt-0 px-0">
                      {report.handler_leaderboard.length === 0 ? (
                        <EmptyState title="No team member activity" />
                      ) : (
                        <Table className="cx-table">
                          <TableHeader>
                            <TableRow>
                              <TableHead className="pl-4">#</TableHead>
                              <TableHead>Team Member</TableHead>
                              <TableHead className="text-right">Genuine</TableHead>
                              <TableHead className="text-right pr-4">Reopens</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {report.handler_leaderboard.map((h) => (
                              <TableRow key={h.handler}>
                                <TableCell className="pl-4 tabular">{h.rank}</TableCell>
                                <TableCell className="max-w-[200px] truncate font-medium">{h.handler}</TableCell>
                                <TableCell className="text-right tabular font-semibold">{h.genuine_closures}</TableCell>
                                <TableCell className="text-right tabular pr-4">{h.reopens}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </CardContent>
                  </Card>

                  <Card data-testid="monthly-top-categories">
                    <CardHeader className="pb-2"><CardTitle className="font-serif text-base font-normal">Top Categories</CardTitle></CardHeader>
                    <CardContent className="pt-0">
                      {report.top_categories.length === 0 ? (
                        <EmptyState title="No complaints this month" />
                      ) : (
                        <div className="space-y-1.5">
                          {report.top_categories.map((c, i) => (
                            <div key={c.category} className="flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-[hsl(var(--cx-surface))]">
                              <span className="flex items-center gap-2 min-w-0">
                                <span className="text-xs text-muted-foreground tabular w-4">{i + 1}</span>
                                <span className="text-sm truncate">{c.category}</span>
                              </span>
                              <span className="tabular text-sm font-semibold">{c.count}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Google Ratings section (all variants) */}
                {report.google_ratings && <MonthlyGoogleRatings section={report.google_ratings} />}
                {report.oem_scorecards && <MonthlyOemSection section={report.oem_scorecards} />}

                {/* MD variant: adoption section (login % by location + month trend) */}
                {report.adoption && (user?.role === "md" || isAdmin) && (
                  <Card data-testid="monthly-adoption-section">
                    <CardHeader className="pb-2">
                      <CardTitle className="font-serif text-base font-normal">Team Adoption — {report.label}</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0 space-y-3">
                      <p className="text-sm">
                        <strong className="font-serif text-xl tabular">{report.adoption.month_pct}%</strong>
                        <span className="text-muted-foreground text-xs"> of active users logged in this month ({report.adoption.logged_in} of {report.adoption.total_active})</span>
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-[0.65rem] uppercase tracking-[0.12em] text-muted-foreground font-medium mb-1.5">Login % by location</p>
                          <div className="space-y-1">
                            {report.adoption.by_location.map((l) => (
                              <div key={l.location} className="flex items-center justify-between gap-2 text-xs px-2 py-1 rounded bg-[hsl(var(--cx-surface))]">
                                <span className="truncate">{l.location}</span>
                                <span className="tabular font-semibold">{l.pct}% <span className="text-muted-foreground font-normal">({l.logged_in}/{l.total})</span></span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <p className="text-[0.65rem] uppercase tracking-[0.12em] text-muted-foreground font-medium mb-1.5">Month trend (weekly)</p>
                          <div className="space-y-1">
                            {report.adoption.weekly_trend.map((w) => (
                              <div key={w.label} className="flex items-center gap-2 text-xs">
                                <span className="w-8 text-muted-foreground">{w.label}</span>
                                <div className="flex-1 h-1.5 rounded-full bg-[hsl(var(--cx-surface))] overflow-hidden">
                                  <div className="h-full rounded-full bg-[hsl(var(--cx-navy))]" style={{ width: `${Math.min(w.pct, 100)}%` }} />
                                </div>
                                <span className="tabular w-10 text-right font-semibold">{w.pct}%</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
